import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from '../design-system/button/Button';
import Mixpanel from '../../utils/mixpanel-helper.js';
import { startGoogleConnect, checkGoogleStatus, disconnectGoogleAccount } from '../../actions/googleAuthActions';

import styles from '../../style/components/account/GoogleAuthComponent.module.scss';

const GoogleAuthComponent = ({
  user,
  token,
  isLoading,
  googleEmail,
  hasError,
  startGoogleConnectAction,
  checkGoogleStatusAction,
  disconnectGoogleAccountAction
}) => {
  // Add state to track connection attempts
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);
  const [debugInfo, setDebugInfo] = useState('No connection attempt yet');

  useEffect(() => {
    if (user && user.user_type === 'tutor') {
      console.log('Checking Google status for user:', user);
      checkGoogleStatusAction(token)
        .then((response) => {
          console.log('Initial Google status check response:', response?.data);
        })
        .catch((err) => {
          console.error('Error checking Google status:', err);
        });
    }

    // Cleanup interval on unmount
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, []);

  // Effect to monitor googleEmail changes
  useEffect(() => {
    console.log('Google email changed:', googleEmail);
    if (googleEmail && statusCheckInterval) {
      console.log('Clearing status check interval due to successful connection');
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
      setDebugInfo('Connection successful! Email: ' + googleEmail);
    }
  }, [googleEmail, statusCheckInterval]);

  // Only show to tutors
  if (!user || user.user_type !== 'tutor') {
    return null;
  }

  const handleGoogleConnect = () => {
    Mixpanel.track('Click', { button: 'connect_google_account' });
    setConnectionAttempted(true);
    setDebugInfo('Initiating connection...');

    startGoogleConnectAction(token)
      .then((response) => {
        console.log('Google connect response:', response);
        setDebugInfo('Please complete the Google OAuth flow in the opened window.');

        // Clear any existing interval
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
        }

        // Poll for connection status periodically after initiating the connection
        const interval = setInterval(() => {
          checkGoogleStatusAction(token)
            .then((response) => {
              console.log('Status check response:', response?.data);
              if (response && response.data && response.data.is_connected) {
                setDebugInfo((prev) => prev + '\nConnection successful!');
                clearInterval(interval);
                setStatusCheckInterval(null);
              }
            })
            .catch((err) => {
              console.error('Error checking status:', err);
              setDebugInfo((prev) => prev + '\nError checking status: ' + (err.message || 'Unknown error'));
            });
        }, 10000); // Check every 1 second

        setStatusCheckInterval(interval);

        // Stop checking after 2 minutes
        setTimeout(() => {
          if (interval) {
            clearInterval(interval);
            setStatusCheckInterval(null);
            setDebugInfo((prev) => prev + '\nConnection timed out. Please refresh the page and try again.');
          }
        }, 120000);
      })
      .catch((err) => {
        console.error('Error starting Google connect:', err);
        setDebugInfo('Error starting connection: ' + (err.message || 'Unknown error'));
      });
  };

  const handleGoogleDisconnect = () => {
    Mixpanel.track('Click', { button: 'disconnect_google_account' });
    setDebugInfo('Disconnecting...');

    disconnectGoogleAccountAction(token)
      .then((response) => {
        console.log('Disconnect response:', response);
        setDebugInfo('Disconnected successfully');
      })
      .catch((err) => {
        console.error('Error disconnecting:', err);
        setDebugInfo('Error disconnecting: ' + (err.message || 'Unknown error'));
      });
  };

  const isConnected = !!googleEmail;

  return (
    <div className={styles['google-auth-container']}>
      <div className={styles['google-auth-header']}>
        <h2>Google Meet Integration</h2>
        <p>Connect your Google account to become the host of your Google Meet sessions</p>
      </div>

      {isConnected ? (
        <>
          <div className={styles['google-auth-connected']}>
            <div className={styles['google-auth-info']}>
              <span className={styles['google-auth-status']}>✅ Connected</span>
              <span className={styles['google-auth-email']}>{googleEmail}</span>
            </div>
            <Button outfit="dangerButton" onClick={handleGoogleDisconnect} loading={isLoading} disabled={isLoading}>
              Disconnect
            </Button>
          </div>
        </>
      ) : (
        <div className={styles['google-auth-disconnected']}>
          <p>
            When connected, you&apos;ll be the host of your Google Meet sessions, allowing you to share your screen with students and control
            participant access
          </p>
          <Button outfit="primaryButton" onClick={handleGoogleConnect} loading={isLoading} disabled={isLoading}>
            Connect with Google
          </Button>
          {hasError && <p className={styles['google-auth-error']}>There was an error connecting your Google account. Please try again.</p>}

          {/* Debug information */}
          {connectionAttempted && (
            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{debugInfo}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GoogleAuthComponent.propTypes = {
  user: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  googleEmail: PropTypes.string,
  hasError: PropTypes.bool,
  startGoogleConnectAction: PropTypes.func.isRequired,
  checkGoogleStatusAction: PropTypes.func.isRequired,
  disconnectGoogleAccountAction: PropTypes.func.isRequired
};

GoogleAuthComponent.defaultProps = {
  isLoading: false,
  googleEmail: '',
  hasError: false
};

const mapStateToProps = (state) => ({
  user: state.user,
  token: state.user.token,
  isLoading: state.user.isLoadingGoogleConnect,
  googleEmail: state.user.google_email || '',
  hasError: state.user.hasErrorGoogleConnect
});

export default connect(mapStateToProps, {
  startGoogleConnectAction: startGoogleConnect,
  checkGoogleStatusAction: checkGoogleStatus,
  disconnectGoogleAccountAction: disconnectGoogleAccount
})(GoogleAuthComponent);
